import React from 'react';
import { Award, Heart, Target } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

const Home = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9 22 9 12 15 12 15 22"></polyline>
  </svg>
);

const About = () => {
  const credentials = [
    'Trained dogs for over ten years',
    'Started at Kenya SPCA training friends\' dogs',
    'Trained vom Huas Berg German Shepherds after college',
    'Degree in Animal Science-Ranch Operations',
    'Multiple dog training certifications',
    'Certified AKC Farm Dog Judge'
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>About Farm Dog Oklahoma — Micah Cox, Professional Dog Trainer | Tulsa OK</title>
        <meta name="description" content="Meet Micah Cox, founder of Farm Dog Oklahoma. Over 10 years of dog training experience, certified AKC Farm Dog Judge, and Animal Science degree. Serving Tulsa, Broken Arrow, Owasso, and all surrounding Oklahoma cities." />
        <link rel="canonical" href="https://farmdogoklahoma.com/about" />
        <meta property="og:title" content="About Farm Dog Oklahoma — Micah Cox" />
        <meta property="og:description" content="Meet Micah Cox — 10+ years experience, AKC certified, transforming rascals into reliable companions." />
        <meta property="og:url" content="https://farmdogoklahoma.com/about" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#5C4033] to-[#3E2723] py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#C4A052] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#C4A052] rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              About Us
            </h1>
            <p className="text-xl md:text-2xl text-[#C4A052] font-semibold">
              Transforming Rascals into Reliable Companions
            </p>
          </div>
        </div>
      </section>

      {/* Main Story Section */}
      <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div variants={fadeInUp} className="order-2 lg:order-1 space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold text-[#5C4033]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Meet Micah Cox
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Micah Cox created Farm Dog Oklahoma when she saw a growing need — many dogs, including her dog, Zane (the inspiration behind our logo!), accompanying their owners to farms, ranches, and barns, lacked the proper training to excel.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Micah has trained dogs for over ten years, starting at Kenya SPCA, training friends' dogs, and training vom Huas Berg German Shepherds after college. With a degree in Animal Science-Ranch Operations, many dog training certifications, and a certified AKC Farm Dog Judge, Micah is devoted to her continual education to best help your dog!
              </p>
              <p className="text-lg text-[#C4A052] font-semibold">
                Shoot us a call chat and find the best solution for your pup!
              </p>
            </motion.div>
            <motion.div variants={fadeInUp} className="order-1 lg:order-2">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#C4A052] to-[#B8935E] rounded-3xl transform rotate-3 opacity-20"></div>
                <img 
                  src="/assets/images/micah-cox.jpg" 
                  alt="Micah Cox with horse and dog" 
                  className="relative rounded-3xl shadow-2xl w-full object-contain"
                />
              </div>
            </motion.div>
          </motion.div>

          {/* Logo Section */}
          <div className="text-center mb-12">
            <div className="flex justify-center items-center gap-8 mb-6">
              <img 
                src="/assets/images/logo-color.jpg" 
                alt="Farm Dog Oklahoma Logo Color" 
                className="h-32 w-32 object-contain hover-lift"
              />
              <img 
                src="/assets/images/logo-bw.jpg" 
                alt="Farm Dog Oklahoma Logo Black" 
                className="h-32 w-32 object-contain hover-lift"
              />
            </div>
            <h3 className="text-2xl font-bold text-[#5C4033] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Farm Dog Oklahoma
            </h3>
            <p className="text-lg text-[#C4A052] font-semibold">
              Helping dogs thrive where the land runs wide
            </p>
          </div>
        </div>
      </motion.section>

      {/* Credentials Section */}
      <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="section-padding bg-[#F5F5F0]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-[#5C4033] mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
              Experience & Credentials
            </h2>
            <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid md:grid-cols-2 gap-4">
              {credentials.map((credential, index) => (
                <motion.div key={index} variants={fadeInUp} className="flex items-center gap-3 bg-white p-4 rounded-lg shadow hover-lift">
                  <Award className="text-[#C4A052] flex-shrink-0" size={24} />
                  <span className="text-gray-700">{credential}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* What We Do Section */}
      <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#5C4033] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              What We Do
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              At Farm Dog Oklahoma, we turn problem dogs into perfect partnerships. Whether your dog chases livestock, struggles with boundaries, or needs better manners at home, our specialized training programs create reliable companions for farm and ranch life.
            </p>
          </div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid md:grid-cols-3 gap-8">
            <motion.div variants={fadeInUp}>
              <Card className="hover-lift border-none shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#C4A052] to-[#B8935E] rounded-full text-white mb-4">
                    <Target size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-[#5C4033] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Practical Skills First
                  </h3>
                  <p className="text-gray-700">We prioritize real farm skills before traditional obedience</p>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div variants={fadeInUp}>
              <Card className="hover-lift border-none shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#C4A052] to-[#B8935E] rounded-full text-white mb-4">
                    <Home size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-[#5C4033] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Home Environment
                  </h3>
                  <p className="text-gray-700">Dogs stay in our homes, not kennels, becoming part of the family</p>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div variants={fadeInUp}>
              <Card className="hover-lift border-none shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#C4A052] to-[#B8935E] rounded-full text-white mb-4">
                    <Heart size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-[#5C4033] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Lifetime Support
                  </h3>
                  <p className="text-gray-700">We're with you every step of the way, even after training ends</p>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Mission Statement */}
      <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="section-padding bg-gradient-to-br from-[#C4A052] to-[#B8935E]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Our Mission
            </h2>
            <p className="text-xl leading-relaxed">
              Through our partnership with All Round Equine, dogs experience daily exposure to farm life while also learning essential house manners in our trainers' homes. This well-rounded experience ensures your dog thrives both on the range and at your side.
            </p>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default About;
